import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import SelectMarketplaceModal from '../../../../../settings/stores/select-marketplace-modal';
import V2MarketplaceSetupModal from '../../../../../settings/stores/v2-modals/marketplace-setup';
import MarketplaceSetupModal from '../../../../../settings/stores/marketplace-connection-modal/setup';
import { HOME_PAGE_PATH } from '../../../../../../routes/constants';
import { getReferenceData } from '../../../../../../store/reference-data/reference-data.actions';
import { refreshStoreList } from '../../../../../../store/seller/seller.actions';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { LocalStorageService } from '../../../../../../services/local-storage-service';
import { CONNECTED_MARKETPLACES_GUIDS } from '../../../../../settings/stores/marketplace-connection-modal/store-connection-success-notifications';
import Global from '../../../../../../store/global-selectors';
import { stepNames } from '../../../../../settings/stores/tracking-events';
import { Workflows } from '../../../../../../tracking/tracking-constants';

export const MarketplaceSelector = ({
  setMarketplaceSelectorOpen,
  setSelectedMarketplace,
  marketplaceSelectorOpen,
  selectedMarketplace,
}) => {
  const dispatch = useDispatch();
  const marketplacesReference = useSelector(Global.selectReferenceDatagetMarketplaces);
  const trackMarketplaceConnection = useTrackWorkflow(Workflows.MARKETPLACE_CONNECTION_FLOW);

  const onSaveNewMarketplace = useCallback(() => {
    const { marketplaceId, name } = selectedMarketplace;
    if (!marketplacesReference.some((market) => market.marketplaceId === marketplaceId)) {
      dispatch(getReferenceData());
    }
    dispatch(refreshStoreList());
    const setupMarketplaceWithoutLogo = {
      ...selectedMarketplace,
      logo: undefined,
    };
    trackMarketplaceConnection(WorkflowSteps.COMPLETE, {
      selectedMarketplace: setupMarketplaceWithoutLogo,
      marketplaceName: name,
      marketplaceId,
    });
    LocalStorageService.clearItemsWithPrefix(CONNECTED_MARKETPLACES_GUIDS);
    setSelectedMarketplace(undefined);
  }, [dispatch, marketplacesReference, selectedMarketplace, setSelectedMarketplace, trackMarketplaceConnection]);

  const onCloseSelectMarketplaceModal = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      stepName: stepNames.SELECT_MARKETPLACE_MODAL,
    });
    setMarketplaceSelectorOpen(false);
  }, [setMarketplaceSelectorOpen, trackMarketplaceConnection]);

  const onMarketplaceSelected = useCallback(
    (marketplace) => {
      setSelectedMarketplace(marketplace);
      setMarketplaceSelectorOpen(false);
      trackMarketplaceConnection(WorkflowSteps.CLICK, {
        selectedMarketplace: marketplace,
        marketplaceName: marketplace?.name,
        marketplaceId: marketplace?.marketplaceId,
      });
    },
    [setMarketplaceSelectorOpen, setSelectedMarketplace, trackMarketplaceConnection],
  );

  const onCloseSetupModal = useCallback(() => {
    const setupMarketplaceWithoutLogo = {
      ...selectedMarketplace,
      logo: undefined,
    };
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      selectedMarketplace: setupMarketplaceWithoutLogo,
      marketplaceName: setupMarketplaceWithoutLogo?.name,
      marketplaceId: setupMarketplaceWithoutLogo?.marketplaceId,
      stepName: stepNames.STORE_SPECIFIC_SETUP_MODAL,
    });
    setSelectedMarketplace(undefined);
  }, [selectedMarketplace, setSelectedMarketplace, trackMarketplaceConnection]);

  const onRedirectToMarketplaceSetup = useCallback(() => {
    const setupMarketplaceWithoutLogo = {
      ...selectedMarketplace,
      logo: undefined,
    };
    trackMarketplaceConnection(WorkflowSteps.REDIRECT, {
      selectedMarketplace: setupMarketplaceWithoutLogo,
      marketplaceName: setupMarketplaceWithoutLogo?.name,
      marketplaceId: setupMarketplaceWithoutLogo?.marketplaceId,
    });
  }, [selectedMarketplace, trackMarketplaceConnection]);

  const onError = useCallback(
    (error) => {
      const errorMessage = error?.error?.ExceptionMessage || error?.message;
      trackMarketplaceConnection(WorkflowSteps.ERROR, {
        error,
        errorMessage,
      });
    },
    [trackMarketplaceConnection],
  );

  const onMarketplaceSetupBack = useCallback(() => {
    setSelectedMarketplace(undefined);
    setMarketplaceSelectorOpen(true);
  }, [setMarketplaceSelectorOpen, setSelectedMarketplace]);

  return (
    <>
      {marketplaceSelectorOpen && (
        <SelectMarketplaceModal onClose={onCloseSelectMarketplaceModal} onMarketplaceSelected={onMarketplaceSelected} />
      )}
      {selectedMarketplace && !selectedMarketplace.isSupported && (
        <V2MarketplaceSetupModal
          marketplaceId={selectedMarketplace.marketplaceId}
          onClose={onCloseSetupModal}
          onSave={onSaveNewMarketplace}
        />
      )}
      {selectedMarketplace && selectedMarketplace.isSupported && (
        <MarketplaceSetupModal
          marketplaceApiCode={selectedMarketplace.apiCode}
          onBack={onMarketplaceSetupBack}
          onClose={onCloseSetupModal}
          onError={onError}
          onRedirect={onRedirectToMarketplaceSetup}
          onSave={onSaveNewMarketplace}
          redirectPath={HOME_PAGE_PATH}
        />
      )}
    </>
  );
};

MarketplaceSelector.propTypes = {
  setMarketplaceSelectorOpen: PropTypes.func.isRequired,
  setSelectedMarketplace: PropTypes.func.isRequired,
  marketplaceSelectorOpen: PropTypes.bool.isRequired,
  selectedMarketplace: PropTypes.object,
};
